<template>
  <AppLayout 
    page-title="AI Agent Marketplace" 
    page-subtitle="Discover and deploy specialized AI agents for oilfield contractors"
  >
    <!-- Marketplace Content -->
    <div class="space-y-8">
      <!-- Search and Filter Section -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div class="flex flex-col lg:flex-row gap-4">
          <!-- Search Bar -->
          <div class="flex-1">
            <div class="relative">
              <input 
                v-model="searchQuery"
                type="text" 
                placeholder="Search AI agents..."
                class="w-full px-4 py-3 pl-10 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <svg class="w-5 h-5 absolute left-3 top-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>
          
          <!-- Category Filter -->
          <div class="flex gap-2">
            <select 
              v-model="selectedCategory"
              class="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              <option value="document">Document Management</option>
              <option value="communication">Communication</option>
              <option value="compliance">Compliance & Safety</option>
              <option value="job">Job Matching</option>
              <option value="contract">Contract Analysis</option>
              <option value="analytics">Analytics & Reporting</option>
            </select>
            
            <select 
              v-model="selectedPrice"
              class="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">All Prices</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Featured Agents -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-semibold text-white mb-4">Featured AI Agents</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="agent in featuredAgents" 
            :key="agent.id"
            class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200 relative"
          >
            <!-- Featured Badge -->
            <div class="absolute top-4 right-4">
              <span class="px-2 py-1 text-xs bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full font-medium">
                Featured
              </span>
            </div>
            
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-lg overflow-hidden">
                <img :src="agent.image" alt="Agent Icon" class="w-full h-full object-cover">
              </div>
              <span class="px-2 py-1 text-xs rounded-full" :class="getStatusClass(agent.status)">
                {{ agent.status }}
              </span>
            </div>
            
            <h4 class="text-lg font-semibold text-white mb-2">{{ agent.name }}</h4>
            <p class="text-sm text-slate-300 mb-4">{{ agent.description }}</p>
            
            <div class="space-y-2 mb-4">
              <div v-for="capability in agent.capabilities.slice(0, 3)" :key="capability" class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                {{ capability }}
              </div>
            </div>
            
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="flex items-center">
                  <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                  <span class="text-sm text-white ml-1">{{ agent.rating }}</span>
                </div>
                <span class="text-sm text-slate-400">({{ agent.reviews }} reviews)</span>
              </div>
              <span class="text-lg font-semibold" :class="agent.price === 'Free' ? 'text-emerald-400' : 'text-white'">
                {{ agent.price }}
              </span>
            </div>
            
            <button 
              @click="deployAgent(agent)" 
              :disabled="deployingAgents.has(agent.id)"
              class="w-full btn-primary mt-4"
            >
              <div v-if="deployingAgents.has(agent.id)" class="flex items-center justify-center">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {{ deploymentStatus[agent.id] || 'Deploying...' }}
              </div>
              <span v-else>Deploy Agent</span>
            </button>
          </div>
        </div>
      </div>

      <!-- All Agents Grid -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-semibold text-white">All AI Agents</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-slate-400">{{ filteredAgents.length }} agents found</span>
            <select 
              v-model="sortBy"
              class="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="name">Sort by Name</option>
              <option value="rating">Sort by Rating</option>
              <option value="price">Sort by Price</option>
              <option value="newest">Sort by Newest</option>
            </select>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="agent in filteredAgents" 
            :key="agent.id"
            class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-lg overflow-hidden">
                <img :src="agent.image" alt="Agent Icon" class="w-full h-full object-cover">
              </div>
              <span class="px-2 py-1 text-xs rounded-full" :class="getStatusClass(agent.status)">
                {{ agent.status }}
              </span>
            </div>
            
            <h4 class="text-lg font-semibold text-white mb-2">{{ agent.name }}</h4>
            <p class="text-sm text-slate-300 mb-4">{{ agent.description }}</p>
            
            <div class="space-y-2 mb-4">
              <div v-for="capability in agent.capabilities.slice(0, 2)" :key="capability" class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                {{ capability }}
              </div>
            </div>
            
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="flex items-center">
                  <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                  <span class="text-sm text-white ml-1">{{ agent.rating }}</span>
                </div>
                <span class="text-sm text-slate-400">({{ agent.reviews }})</span>
              </div>
              <span class="text-lg font-semibold" :class="agent.price === 'Free' ? 'text-emerald-400' : 'text-white'">
                {{ agent.price }}
              </span>
            </div>
            
            <button @click="deployAgent(agent)" class="w-full btn-primary mt-4">
              Deploy Agent
            </button>
          </div>
        </div>
        
        <!-- Empty State -->
        <div v-if="filteredAgents.length === 0" class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <p class="text-slate-400 text-lg">No AI agents found</p>
          <p class="text-slate-500 text-sm">Try adjusting your search or filters</p>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()
const router = useRouter()

// Reactive data
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedPrice = ref('')
const sortBy = ref('name')

// Reactive data for deployment status
const deployingAgents = ref<Set<string>>(new Set())
const deploymentStatus = ref<Record<string, string>>({})

// AI Agents data with real agentic abilities
const allAgents = ref([
  {
    id: 'doc-assistant-001',
    name: 'Document Assistant',
    category: 'document',
    description: 'Create, edit, and format professional documents including safety reports, compliance forms, and project documentation.',
    status: 'ready',
    price: 'Free',
    rating: 4.8,
    reviews: 156,
    iconBg: 'bg-gradient-to-r from-blue-500 to-purple-500',
    image: '/src/assets/images/bot-avatars/Document Assistant.png',
    capabilities: [
      'Safety reports & incident documentation',
      'Compliance forms & regulatory documents',
      'Project proposals & technical specifications',
      'Contract templates & legal documents'
    ],
    featured: true,
    agenticAbilities: {
      type: 'document-assistant',
      trainingData: 'Oilfield safety protocols, OSHA regulations, project documentation standards, legal contract templates',
      specializations: ['Safety Documentation', 'Compliance Forms', 'Project Proposals', 'Legal Contracts'],
      aiModel: 'GPT-4 with document processing capabilities',
      memory: 'Long-term document template memory',
      reasoning: 'Context-aware document generation and formatting'
    }
  },
  {
    id: 'email-writer-001',
    name: 'Email Writer',
    category: 'communication',
    description: 'Compose professional emails for client communication, project updates, and business correspondence.',
    status: 'ready',
    price: 'Free',
    rating: 4.7,
    reviews: 203,
    iconBg: 'bg-gradient-to-r from-green-500 to-teal-500',
    image: '/src/assets/images/bot-avatars/Email Writer.png',
    capabilities: [
      'Client communication & proposals',
      'Project status updates',
      'Professional follow-ups',
      'Email templates & automation'
    ],
    featured: true,
    agenticAbilities: {
      type: 'email-writer',
      trainingData: 'Professional email etiquette, oilfield industry terminology, client communication best practices',
      specializations: ['Client Communication', 'Project Updates', 'Proposal Writing', 'Follow-up Automation'],
      aiModel: 'GPT-4 with email optimization',
      memory: 'Client communication history and preferences',
      reasoning: 'Tone-aware email generation based on recipient and context'
    }
  },
  {
    id: 'compliance-checker-001',
    name: 'Compliance Checker',
    category: 'compliance',
    description: 'Verify regulatory compliance, safety standards, and industry requirements for oilfield operations.',
    status: 'ready',
    price: 'Premium',
    rating: 4.9,
    reviews: 89,
    iconBg: 'bg-gradient-to-r from-red-500 to-pink-500',
    image: '/src/assets/images/bot-avatars/Compliance Checker.png',
    capabilities: [
      'OSHA compliance verification',
      'Environmental regulations',
      'Industry best practices',
      'Audit preparation & reporting'
    ],
    featured: true,
    agenticAbilities: {
      type: 'compliance-checker',
      trainingData: 'OSHA regulations, EPA guidelines, industry safety standards, audit procedures',
      specializations: ['Regulatory Compliance', 'Safety Audits', 'Environmental Standards', 'Risk Assessment'],
      aiModel: 'GPT-4 with regulatory knowledge base',
      memory: 'Compliance history and audit findings',
      reasoning: 'Real-time compliance analysis and risk assessment'
    }
  },
  {
    id: 'safety-advisor-001',
    name: 'Safety Advisor',
    category: 'compliance',
    description: 'Provide safety guidance, risk assessments, and emergency procedures for oilfield operations.',
    status: 'ready',
    price: 'Free',
    rating: 4.6,
    reviews: 134,
    iconBg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
    image: '/src/assets/images/bot-avatars/Safety Advisor.png',
    capabilities: [
      'Risk assessment & mitigation',
      'Emergency response procedures',
      'Safety training recommendations',
      'Incident analysis & reporting'
    ],
    featured: false,
    agenticAbilities: {
      type: 'safety-advisor',
      trainingData: 'Oilfield safety protocols, emergency response procedures, risk assessment methodologies',
      specializations: ['Risk Assessment', 'Emergency Procedures', 'Safety Training', 'Incident Analysis'],
      aiModel: 'GPT-4 with safety knowledge base',
      memory: 'Safety incident history and lessons learned',
      reasoning: 'Proactive safety recommendations and risk mitigation strategies'
    }
  },
  {
    id: 'job-matcher-001',
    name: 'Job Matcher',
    category: 'job',
    description: 'Match contractors with suitable job opportunities based on skills, experience, and location preferences.',
    status: 'ready',
    price: 'Premium',
    rating: 4.5,
    reviews: 78,
    iconBg: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    image: '/src/assets/images/bot-avatars/Job Matcher.png',
    capabilities: [
      'Skill-based job matching',
      'Location & travel preferences',
      'Salary & benefits analysis',
      'Career path recommendations'
    ],
    featured: false,
    agenticAbilities: {
      type: 'job-matcher',
      trainingData: 'Job market data, skill requirements, salary benchmarks, career progression patterns',
      specializations: ['Job Matching', 'Career Planning', 'Salary Analysis', 'Skill Assessment'],
      aiModel: 'GPT-4 with job market intelligence',
      memory: 'User preferences and job history',
      reasoning: 'Intelligent job matching based on skills, experience, and preferences'
    }
  },
  {
    id: 'contract-analyzer-001',
    name: 'Contract Analyzer',
    category: 'contract',
    description: 'Analyze contracts, identify key terms, risks, and opportunities for oilfield service agreements.',
    status: 'ready',
    price: 'Enterprise',
    rating: 4.8,
    reviews: 45,
    iconBg: 'bg-gradient-to-r from-cyan-500 to-blue-500',
    image: '/src/assets/images/bot-avatars/Contract Analyzer.png',
    capabilities: [
      'Risk assessment & mitigation',
      'Payment terms & schedules',
      'Liability & insurance clauses',
      'Contract optimization suggestions'
    ],
    featured: false,
    agenticAbilities: {
      type: 'contract-analyzer',
      trainingData: 'Contract law, oilfield service agreements, risk assessment frameworks, legal precedents',
      specializations: ['Contract Analysis', 'Risk Assessment', 'Legal Review', 'Negotiation Support'],
      aiModel: 'GPT-4 with legal knowledge base',
      memory: 'Contract history and negotiation outcomes',
      reasoning: 'Legal analysis and risk assessment with optimization recommendations'
    }
  },
  {
    id: 'report-generator-001',
    name: 'Report Generator',
    category: 'analytics',
    description: 'Generate comprehensive reports for project management, safety compliance, and business analytics.',
    status: 'ready',
    price: 'Premium',
    rating: 4.4,
    reviews: 67,
    iconBg: 'bg-gradient-to-r from-purple-500 to-pink-500',
    image: '/src/assets/images/bot-avatars/Report Generator.png',
    capabilities: [
      'Project progress reports',
      'Safety incident reports',
      'Financial analytics',
      'Custom report templates'
    ],
    featured: false,
    agenticAbilities: {
      type: 'report-generator',
      trainingData: 'Report templates, data visualization, analytics methodologies, business intelligence',
      specializations: ['Report Generation', 'Data Analysis', 'Visualization', 'Business Intelligence'],
      aiModel: 'GPT-4 with analytics capabilities',
      memory: 'Report templates and user preferences',
      reasoning: 'Intelligent report generation with data analysis and insights'
    }
  },
  {
    id: 'scheduling-optimizer-001',
    name: 'Scheduling Optimizer',
    category: 'analytics',
    description: 'Optimize crew scheduling, resource allocation, and project timelines for maximum efficiency.',
    status: 'ready',
    price: 'Enterprise',
    rating: 4.7,
    reviews: 92,
    iconBg: 'bg-gradient-to-r from-emerald-500 to-green-500',
    image: '/src/assets/images/bot-avatars/Scheduling Optimizer.png',
    capabilities: [
      'Crew scheduling optimization',
      'Resource allocation',
      'Project timeline management',
      'Cost optimization analysis'
    ],
    featured: false,
    agenticAbilities: {
      type: 'scheduling-optimizer',
      trainingData: 'Scheduling algorithms, resource management, project management methodologies, cost optimization',
      specializations: ['Schedule Optimization', 'Resource Management', 'Project Planning', 'Cost Analysis'],
      aiModel: 'GPT-4 with optimization algorithms',
      memory: 'Schedule history and optimization results',
      reasoning: 'Intelligent scheduling optimization with cost and efficiency analysis'
    }
  }
])

// Computed properties
const filteredAgents = computed(() => {
  let filtered = allAgents.value

  // Search filter
  if (searchQuery.value) {
    filtered = filtered.filter(agent => 
      agent.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      agent.capabilities.some(cap => cap.toLowerCase().includes(searchQuery.value.toLowerCase()))
    )
  }

  // Category filter
  if (selectedCategory.value) {
    filtered = filtered.filter(agent => agent.category === selectedCategory.value)
  }

  // Price filter
  if (selectedPrice.value) {
    filtered = filtered.filter(agent => {
      if (selectedPrice.value === 'free') return agent.price === 'Free'
      if (selectedPrice.value === 'premium') return agent.price === 'Premium'
      if (selectedPrice.value === 'enterprise') return agent.price === 'Enterprise'
      return true
    })
  }

  // Sort
  filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'rating':
        return b.rating - a.rating
      case 'price':
        return a.price.localeCompare(b.price)
      case 'newest':
        return b.id.localeCompare(a.id)
      default:
        return 0
    }
  })

  return filtered
})

const featuredAgents = computed(() => {
  return allAgents.value.filter(agent => agent.featured)
})

// Methods
const getStatusClass = (status: string) => {
  switch (status) {
    case 'ready': return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'training': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
    case 'beta': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
    default: return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
}

const deployAgent = async (agent: any) => {
  if (deployingAgents.value.has(agent.id)) return
  
  deployingAgents.value.add(agent.id)
  deploymentStatus.value[agent.id] = 'Initializing deployment...'
  
  try {
    // Step 1: Create the agent with agentic abilities
    const createResponse = await fetch('/api/ai/agents/create', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: agent.name,
        type: agent.agenticAbilities.type,
        description: agent.description,
        trainingData: agent.agenticAbilities.trainingData,
        specializations: agent.agenticAbilities.specializations,
        aiModel: agent.agenticAbilities.aiModel,
        memory: agent.agenticAbilities.memory,
        reasoning: agent.agenticAbilities.reasoning,
        capabilities: agent.capabilities,
        userId: userStore.currentUser?.id
      })
    })
    
    if (!createResponse.ok) {
      throw new Error('Failed to create agent')
    }
    
    const createResult = await createResponse.json()
    deploymentStatus.value[agent.id] = 'Agent created, initializing training...'
    
    // Step 2: Deploy the agent with its agentic abilities
    const deployResponse = await fetch('/api/ai/agents/deploy', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        agentId: createResult.agent.id,
        agenticAbilities: agent.agenticAbilities,
        deploymentConfig: {
          enableMemory: true,
          enableReasoning: true,
          enableSpecializations: true,
          trainingMode: 'continuous',
          autoOptimize: true
        },
        userId: userStore.currentUser?.id
      })
    })
    
    if (!deployResponse.ok) {
      throw new Error('Failed to deploy agent')
    }
    
    const deployResult = await deployResponse.json()
    deploymentStatus.value[agent.id] = 'Agent deployed successfully!'
    
    // Step 3: Initialize agentic abilities
    const initResponse = await fetch('/api/ai/agents/initialize', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        agentId: createResult.agent.id,
        agenticAbilities: {
          type: agent.agenticAbilities.type,
          trainingData: agent.agenticAbilities.trainingData,
          specializations: agent.agenticAbilities.specializations,
          aiModel: agent.agenticAbilities.aiModel,
          memory: agent.agenticAbilities.memory,
          reasoning: agent.agenticAbilities.reasoning
        },
        userId: userStore.currentUser?.id
      })
    })
    
    if (initResponse.ok) {
      deploymentStatus.value[agent.id] = 'Agentic abilities initialized!'
      
      // Show success message
      setTimeout(() => {
        // Navigate back to AI Messaging page after successful deployment
        router.push('/ai-messaging')
      }, 2000)
    } else {
      deploymentStatus.value[agent.id] = 'Deployed but initialization failed'
    }
    
  } catch (error) {
    console.error('Error deploying agent:', error)
    deploymentStatus.value[agent.id] = 'Deployment failed'
    
    // Show error message to user
    alert(`Failed to deploy ${agent.name}. Please try again.`)
  } finally {
    deployingAgents.value.delete(agent.id)
  }
}

// Lifecycle
onMounted(async () => {
  try {
    await userStore.loadUserFromStorage()
  } catch (error) {
    console.error('AI Marketplace page mount error:', error)
  }
})
</script>

<style scoped>
.btn-primary {
  @apply px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
